/*
 * Sukarah (TASK-126): announcement bar animation styles.
 *
 * Builds on Prestige's own primitives instead of replacing them:
 *  - <sukarah-announcement-carousel> extends the exported AnnouncementBarCarousel,
 *    adding an optional pause-on-hover (via the carousel's public player API) and a
 *    horizontal "slide" transition (effect="slide") that follows the document
 *    direction. Without effect="slide" the transition is exactly the stock one.
 *  - <sukarah-announcement-marquee> powers the marquee/ticker styles: it measures
 *    the message group once, clones it just enough times for a seamless loop, and
 *    hands the animation to a CSS transform keyframe (no per-frame JS). Clones are
 *    aria-hidden and their links removed from the tab order, so screen readers and
 *    keyboard users only ever meet each message once.
 *
 * Reduced motion: the carousel styles have their autoplay attribute stripped by an
 * inline guard in the section before upgrade; the marquee skips cloning here and
 * falls back to the static first message via CSS.
 */
import { AnnouncementBarCarousel } from 'theme';
import { animate } from 'vendor';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const CAN_HOVER = window.matchMedia('(hover: hover)');

class SukarahAnnouncementCarousel extends AnnouncementBarCarousel {
  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('pause-on-hover') && CAN_HOVER.matches && !this._sukarahHoverBound) {
      this._sukarahHoverBound = true;
      this.addEventListener('mouseenter', () => this.player?.pause());
      this.addEventListener('mouseleave', () => this.player?.resume());
    }
  }

  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    if (this.getAttribute('effect') !== 'slide') {
      return super.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
    }

    // "next" exits toward the inline start and enters from the inline end,
    // mirrored automatically in RTL documents.
    const inlineFlip = document.documentElement.dir === 'rtl' ? -1 : 1;
    const exitSign = (direction === 'previous' ? 1 : -1) * inlineFlip;

    return {
      leaveControls: () => animate(
        fromSlide,
        { opacity: [1, 0], transform: ['translateX(0px)', `translateX(${exitSign * 24}px)`] },
        { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }
      ),
      enterControls: () => animate(
        toSlide,
        { opacity: [0, 1], transform: [`translateX(${-exitSign * 24}px)`, 'translateX(0px)'] },
        { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] }
      )
    };
  }
}

if (!window.customElements.get('sukarah-announcement-carousel')) {
  window.customElements.define('sukarah-announcement-carousel', SukarahAnnouncementCarousel);
}

class SukarahAnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this._track = this.querySelector('.announcement-bar__marquee-track');

    if (!this._track) {
      return;
    }

    // Idempotent across Theme Editor re-renders and reconnects.
    this._track.querySelectorAll('[data-sukarah-clone]').forEach((clone) => clone.remove());
    this._originals = Array.from(this._track.children);

    if (REDUCED_MOTION.matches) {
      return; // CSS shows the static fallback; no clones needed.
    }

    this._build();

    this._resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this._build());
    });
    this._resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    cancelAnimationFrame(this._raf);
  }

  _build() {
    const containerWidth = this.clientWidth;

    if (containerWidth === 0) {
      return;
    }

    this.removeAttribute('data-ready');
    this._track.querySelectorAll('[data-sukarah-clone]').forEach((clone) => clone.remove());

    const gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
    const groupWidth = this._originals.reduce((width, el) => width + el.getBoundingClientRect().width + gap, 0);

    if (groupWidth === 0) {
      return;
    }

    // One animation period (= copiesPerHalf groups) must cover the container with a
    // one-group margin, so the loop boundary is never visible.
    const copiesPerHalf = Math.max(1, Math.ceil((containerWidth + groupWidth) / groupWidth));
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < copiesPerHalf * 2 - 1; i++) { // originals already form the first group
      this._originals.forEach((el) => {
        const clone = el.cloneNode(true);
        clone.setAttribute('data-sukarah-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, [tabindex]').forEach((focusable) => focusable.setAttribute('tabindex', '-1'));
        fragment.appendChild(clone);
      });
    }

    this._track.appendChild(fragment);

    const distance = groupWidth * copiesPerHalf;
    const pxPerSecond = parseFloat(this.getAttribute('speed')) || 75;
    this.style.setProperty('--sukarah-marquee-distance', `${distance}px`);
    this.style.setProperty('--sukarah-marquee-duration', `${Math.max(distance / pxPerSecond, 5)}s`);
    this.setAttribute('data-ready', '');
  }
}

if (!window.customElements.get('sukarah-announcement-marquee')) {
  window.customElements.define('sukarah-announcement-marquee', SukarahAnnouncementMarquee);
}
